import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";

import { TEAM_INTERACTION_SOURCE } from "./teamInteraction";
import { acceptTeamInvitation } from "./teamInvitationActions";
import { clearTeamJoin, pendingTeamJoin, teamJoinUnavailableNotice } from "./teamJoinContinuation";

type InvitationUser = Pick<UserDetails, "uuid" | "is_teams_enabled" | "team_invitation_check_id">;
type Completion = "not_invited" | "complete" | "failed";
type Destination = { url: string; unavailable: boolean };

export function createTeamInviteOnboarding(user: () => InvitationUser | undefined) {
    let destination: Destination | null = null;
    let onboardingComplete = false;

    async function finish(): Promise<Completion> {
        const invitation = pendingTeamJoin();
        if (!destination) {
            if (!invitation) {
                return "not_invited";
            }
            const currentUser = user();
            if (currentUser?.is_teams_enabled === false) {
                clearTeamJoin(invitation.request_id);
                destination = { url: "/", unavailable: false };
            } else {
                const result = await acceptTeamInvitation(
                    invitation,
                    `${currentUser?.uuid ?? ""}:${currentUser?.team_invitation_check_id ?? ""}`,
                    TEAM_INTERACTION_SOURCE.TEAM_INVITATION,
                );
                if (result.status === "failed" || result.status === "onboarding_required") {
                    return "failed";
                }
                destination = result.status === "joined" ? { url: result.teamUrl, unavailable: false } : { url: "/people/?view=teams", unavailable: true };
            }
        }
        const target = destination;
        return new Promise<Completion>((resolve) => {
            router.visit(target.url, {
                replace: true,
                onSuccess: () => {
                    destination = null;
                    if (target.unavailable) {
                        teamJoinUnavailableNotice.value = true;
                    }
                },
                onFinish: () => resolve(destination === null ? "complete" : "failed"),
            });
        });
    }

    async function complete(onboardingUrl: string, intent: "done" | "skip" = "done"): Promise<Completion> {
        if (destination) {
            return finish();
        }
        if (!pendingTeamJoin()) {
            return "not_invited";
        }
        if (!onboardingComplete) {
            const { error } = await useFetch(onboardingUrl).post({ intent }).text();
            if (error.value) {
                return "failed";
            }
            onboardingComplete = true;
        }
        return finish();
    }

    return { complete, finish };
}
